// Состояние раздела в адресе (/plan?platform=…&board=…&view=…) и запомненное
// в localStorage `cf2.plan.v1`. Даты и открытая карточка не запоминаются.

import type { PlanPlatform } from '../../api/types'
import type { CalMode } from './calendar.ts'
import { ZOOMS, type GanttZoom } from './ganttScale.ts'
import { isDay, type Day } from './msk.ts'

export type PlanView = 'kanban' | 'calendar' | 'gantt'
export type GanttGroup = 'cards' | 'stages' | 'people'

export const PLATFORMS: readonly PlanPlatform[] = ['telegram', 'instagram']
export const VIEWS: readonly PlanView[] = ['kanban', 'calendar', 'gantt']
export const CAL_MODES: readonly CalMode[] = ['month', 'week']
export const GROUPS: readonly GanttGroup[] = ['cards', 'stages', 'people']

export interface PlanUrlState {
  platform: PlanPlatform
  /** null — «Все» (или единственная доска площадки). */
  board: number | null
  view: PlanView
  cal: CalMode
  /** Опорный день календаря. */
  date: Day | null
  /** Первый день Ганта. */
  from: Day | null
  group: GanttGroup
  card: number | null
}

export interface Remembered {
  platform: PlanPlatform
  board: Record<PlanPlatform, number | null>
  view: PlanView
  cal: CalMode
  group: GanttGroup
  /** Масштаб Ганта; null — не выбирали (телефон «Крупно», компьютер «Средне»). В адрес не идёт. */
  zoom: GanttZoom | null
}

export const DEFAULT_REMEMBERED: Remembered = {
  platform: 'instagram',
  board: { telegram: null, instagram: null },
  view: 'kanban',
  cal: 'week',
  group: 'cards',
  zoom: null,
}

export const STORAGE_KEY = 'cf2.plan.v1'

function oneOf<T extends string>(list: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : undefined
}

function positiveInt(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? value : undefined
  if (typeof value !== 'string' || !/^\d{1,15}$/.test(value)) return undefined
  const n = Number(value)
  return n > 0 ? n : undefined
}

/** В адресе нет ни одного параметра раздела: заполнить из запомненного через replace. */
export function isBarePlanUrl(params: URLSearchParams): boolean {
  return !['platform', 'board', 'view', 'cal', 'date', 'from', 'group', 'card'].some((k) => params.has(k))
}

/**
 * Разбор адреса. Мусор отбрасывается. Чего нет — из запомненного; доска —
 * только если и площадка не указана (иначе «Все»).
 */
export function parsePlanUrl(params: URLSearchParams, remembered: Remembered = DEFAULT_REMEMBERED): PlanUrlState {
  const urlPlatform = oneOf(PLATFORMS, params.get('platform'))
  const platform = urlPlatform ?? remembered.platform
  const board = positiveInt(params.get('board')) ?? (urlPlatform ? null : remembered.board[platform])
  const date = params.get('date')
  const from = params.get('from')
  return {
    platform,
    board: board ?? null,
    view: oneOf(VIEWS, params.get('view')) ?? remembered.view,
    cal: oneOf(CAL_MODES, params.get('cal')) ?? remembered.cal,
    date: isDay(date) ? date : null,
    from: isDay(from) ? from : null,
    group: oneOf(GROUPS, params.get('group')) ?? remembered.group,
    card: positiveInt(params.get('card')) ?? null,
  }
}

export function buildPlanUrl(s: PlanUrlState): URLSearchParams {
  const q = new URLSearchParams()
  q.set('platform', s.platform)
  if (s.board !== null) q.set('board', String(s.board))
  q.set('view', s.view)
  if (s.view === 'calendar') {
    q.set('cal', s.cal)
    if (s.date) q.set('date', s.date)
  }
  if (s.view === 'gantt') {
    if (s.from) q.set('from', s.from)
    q.set('group', s.group)
  }
  if (s.card !== null) q.set('card', String(s.card))
  return q
}

/** Что запомнить из текущего состояния. Масштаба в адресе нет — он остаётся прежним. */
export function rememberFrom(s: PlanUrlState, prev: Remembered): Remembered {
  return {
    platform: s.platform,
    board: { ...prev.board, [s.platform]: s.board },
    view: s.view,
    cal: s.cal,
    group: s.group,
    zoom: prev.zoom,
  }
}

export function parseRemembered(raw: string | null): Remembered {
  if (!raw) return DEFAULT_REMEMBERED
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return DEFAULT_REMEMBERED
  }
  if (!data || typeof data !== 'object') return DEFAULT_REMEMBERED
  const o = data as Record<string, unknown>
  const boards = o.board && typeof o.board === 'object' ? (o.board as Record<string, unknown>) : {}
  return {
    platform: oneOf(PLATFORMS, o.platform) ?? DEFAULT_REMEMBERED.platform,
    board: { telegram: positiveInt(boards.telegram) ?? null, instagram: positiveInt(boards.instagram) ?? null },
    view: oneOf(VIEWS, o.view) ?? DEFAULT_REMEMBERED.view,
    cal: oneOf(CAL_MODES, o.cal) ?? DEFAULT_REMEMBERED.cal,
    group: oneOf(GROUPS, o.group) ?? DEFAULT_REMEMBERED.group,
    zoom: oneOf(ZOOMS, o.zoom) ?? null,
  }
}

/** Ссылка на карточку: «Открыть» из карточки поста, «Скопировать ссылку». Вид — из запомненного. */
export function cardHref(platform: PlanPlatform, board: number, card: number): string {
  return `/plan?platform=${platform}&board=${board}&card=${card}`
}
